// interceptor/auth-interceptor.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { resolveOrganizationAdmin } from '../shared/utils/org-admin.util';
import { environment } from '../../environment/environment';
import { isAppVersionRequestUrl } from '../services/app-version/app-version-check.service';
import { ASSOCIATE_STORAGE_KEYS } from '../associate/associate.constants';
import { ASSOCIATE_HTTP, isAssociateHttpRequest } from '../associate/auth/associate-http.context';

const AZURE_STORAGE_DOMAIN = 'avyrostorage.blob.core.windows.net';
/** Finance → My Finance submodule id (permissions.modules[].subModules). */
const MY_FINANCE_SUBMODULE_ID = 13;

type PermissionsModule = {
  id?: number;
  hasAccess?: boolean;
  recordAccess?: { recordScope?: string | null };
  subModules?: Array<{
    id?: number;
    actions?: Array<{ recordScope?: string | null }>;
  }>;
};

type StoredPermissions = {
  issystemRole?: boolean;
  modules?: PermissionsModule[];
};

function isTrackerAdminRequest(url: string): boolean {
  return url.includes('/tracker/api/admin/');
}

@Injectable()
export class AuthInterceptorInterceptor implements HttpInterceptor {
  constructor(private readonly cookieService: CookieService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.url.includes(AZURE_STORAGE_DOMAIN) || isAppVersionRequestUrl(req.url)) {
      return next.handle(req);
    }

    const associateMode = req.context.get(ASSOCIATE_HTTP);
    if (isAssociateHttpRequest(associateMode)) {
      if (associateMode === 'public') {
        return next.handle(req);
      }
      const associateToken = this.readAssociateToken();
      if (!associateToken) {
        return next.handle(req);
      }
      return next.handle(
        req.clone({
          setHeaders: {
            Authorization: `Bearer ${associateToken}`,
            Accept: req.responseType === 'blob' ? '*/*' : 'application/json',
          },
        }),
      );
    }

    if (isTrackerAdminRequest(req.url)) {
      const adminKey = environment.trackerAdminApiKey?.trim() ?? '';
      if (adminKey) {
        return next.handle(
          req.clone({
            setHeaders: {
              Authorization: `Bearer ${adminKey}`,
              'X-Admin-Api-Key': adminKey,
              Accept: 'application/json',
            },
          }),
        );
      }
    }

    const token = this.cookieService.get('auth_token')?.trim();
    if (!token) {
      return next.handle(req);
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: req.responseType === 'blob' ? '*/*' : 'application/json',
    };

    const raRecordScope = this.resolveRaRecordScope();
    if (raRecordScope) {
      headers['raRecordScope'] = raRecordScope;
    }

    return next.handle(req.clone({ setHeaders: headers }));
  }

  /**
   * - System / org admin (`issystemRole`): `all`
   * - Non-admin with My Finance (subModule id 13): finance module/action `recordScope`, else `finance`
   * - Otherwise: omit header
   */
  private resolveRaRecordScope(): string | null {
    let permissions: StoredPermissions | null = null;
    try {
      const permissionsRaw = localStorage.getItem('permissions');
      if (!permissionsRaw) return null;
      permissions = JSON.parse(permissionsRaw) as StoredPermissions;
    } catch {
      return null;
    }

    if (permissions?.issystemRole === true) {
      return 'all';
    }

    const financeModule = (permissions?.modules ?? []).find(
      (m) =>
        m?.hasAccess === true &&
        Array.isArray(m.subModules) &&
        m.subModules.some((s) => Number(s?.id) === MY_FINANCE_SUBMODULE_ID),
    );
    if (!financeModule) {
      return null;
    }

    const moduleScope = String(financeModule.recordAccess?.recordScope ?? '').trim();
    if (moduleScope) {
      return moduleScope;
    }

    const myFinance = financeModule.subModules?.find(
      (s) => Number(s?.id) === MY_FINANCE_SUBMODULE_ID,
    );
    const actionScope = String(
      myFinance?.actions?.find((a) => a?.recordScope != null && String(a.recordScope).trim() !== '')
        ?.recordScope ?? '',
    ).trim();
    if (actionScope) {
      return actionScope;
    }

    return 'finance';
  }

  private readAssociateToken(): string {
    try {
      return localStorage.getItem(ASSOCIATE_STORAGE_KEYS.authToken)?.trim() ?? '';
    } catch {
      return '';
    }
  }
}
